// MAHUKAA - UNIVERSAL SPEED BOOSTER & ERROR FIX
(function() {
    'use strict';
    
    // DETECT DEVICE & BROWSER
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isSlowConnection = navigator.connection ? 
        (navigator.connection.saveData || navigator.connection.effectiveType.includes('2g')) : false;
    
    // OPTIMIZED CONFIG FOR ALL DEVICES
    const CONFIG = {
        LAZYLOAD_MARGIN: isIOS ? '300px' : '200px', // More margin for iOS
        LOAD_DELAY: isSlowConnection ? 200 : 50,
        RETRY_ATTEMPTS: 3,
        CACHE_NAME: 'mahukaa-assets-v1'
    };
    
    // UNIVERSAL LAZY LOAD STYLES
    const lazyStyles = `
        .lazyload {
            opacity: 0;
            transition: opacity 0.4s ease-in-out;
        }
        .lazyloaded {
            opacity: 1;
        }
        /* iOS Safari specific optimizations */
        .lazyload {
            -webkit-backface-visibility: hidden;
            -webkit-transform: translateZ(0);
        }
        /* Critical images - load immediately */
        .hero-banner img,
        .hero-slide img, 
        .slideshow img,
        .hero-section img,
        .header__logo img,
        .site-logo img {
            opacity: 1 !important;
            transition: none !important;
        }
        /* Reduce motion for accessibility */
        @media (prefers-reduced-motion: reduce) {
            .lazyload {
                transition: none;
            }
        }
    `;
    
    // SAFE STYLE INJECTION
    function injectStyles() {
        try {
            if (document.head) {
                const styleSheet = document.createElement('style');
                styleSheet.textContent = lazyStyles;
                document.head.appendChild(styleSheet);
            }
        } catch (e) {
            // Silent fail for styles
        }
    }
    
    // UNIVERSAL LAZY LOAD CLASS
    class MahukaaLazyLoad {
        constructor() {
            this.observer = null;
            this.loadedImages = new Set();
            this.retryCounts = new Map();
            this.init();
        }
        
        init() {
            console.log('Mahukaa Universal Lazy Load Started');
            this.loadVisibleImages();
            this.setupLazyLoad();
        }
        
        // Load images already in viewport immediately
        loadVisibleImages() {
            const images = document.querySelectorAll('img[data-src].lazyload');
            images.forEach(img => {
                if (this.isInViewport(img) && this.shouldLoadImage(img)) {
                    this.loadImage(img);
                }
            });
        }
        
        // Check if element is in viewport
        isInViewport(element) {
            const rect = element.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            return (
                rect.top >= -100 && 
                rect.bottom <= windowHeight + 300
            );
        }
        
        // Check if we should load this image
        shouldLoadImage(img) {
            // Skip hero banners and critical sections
            if (img.closest('.hero-banner') || 
                img.closest('.hero-slide') || 
                img.closest('.slideshow') || 
                img.closest('.hero-section') ||
                img.closest('.header__logo') ||
                img.closest('.site-logo')) {
                return false;
            }
            
            // Only target product and collection images
            const parent = img.closest('a, div, li');
            if (parent) {
                const href = parent.getAttribute('href') || '';
                const className = parent.className || '';
                if (href.includes('/products/') || 
                    href.includes('/collections/') ||
                    className.includes('product') ||
                    className.includes('collection')) {
                    return true;
                }
            }
            
            return false;
        }
        
        setupLazyLoad() {
            // Use IntersectionObserver if available
            if ('IntersectionObserver' in window) {
                this.setupObserver();
            } else {
                // Fallback for older browsers
                this.setupScrollListener();
            }
        }
        
        setupObserver() {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        if (this.shouldLoadImage(img)) {
                            this.loadImage(img);
                            this.observer.unobserve(img);
                        }
                    }
                });
            }, {
                rootMargin: CONFIG.LAZYLOAD_MARGIN,
                threshold: 0.1
            });
            
            this.observeImages();
        }
        
        observeImages() {
            const images = document.querySelectorAll('img[data-src].lazyload');
            images.forEach(img => {
                if (!this.loadedImages.has(img) && this.shouldLoadImage(img)) {
                    this.observer.observe(img);
                }
            });
        }
        
        setupScrollListener() {
            let ticking = false;
            
            const checkImages = () => {
                const images = document.querySelectorAll('img[data-src].lazyload');
                images.forEach(img => {
                    if (!this.loadedImages.has(img) && 
                        this.shouldLoadImage(img) && 
                        this.isInViewport(img)) {
                        this.loadImage(img);
                    }
                });
                ticking = false;
            };
            
            const onScroll = () => {
                if (!ticking) {
                    requestAnimationFrame(checkImages);
                    ticking = true;
                }
            };
            
            window.addEventListener('scroll', onScroll, { passive: true });
            window.addEventListener('resize', onScroll, { passive: true });
            
            // Initial check
            setTimeout(checkImages, CONFIG.LOAD_DELAY);
        }
        
        loadImage(element) {
            if (this.loadedImages.has(element)) return;
            
            this.loadedImages.add(element);
            const src = element.dataset.src;
            
            if (!src) return;
            
            const img = new Image();
            
            img.onload = () => {
                element.src = src;
                element.classList.remove('lazyload');
                element.classList.add('lazyloaded');
                
                // iOS Safari memory optimization
                if (isIOS) {
                    element.decode().catch(() => {});
                }
            };
            
            img.onerror = () => {
                this.retryLoad(element);
            };
            
            // Start loading
            img.src = src;
        }
        
        retryLoad(element) {
            const retries = this.retryCounts.get(element) || 0;
            
            if (retries < CONFIG.RETRY_ATTEMPTS) {
                this.retryCounts.set(element, retries + 1);
                this.loadedImages.delete(element);
                
                setTimeout(() => {
                    this.loadImage(element);
                }, 1000 * (retries + 1));
            } else {
                console.warn('Failed to load image after retries:', element.dataset.src);
            }
        }
    }
    
    // UNIVERSAL ERROR FIXES
    function fixAllErrors() {
        // Safe classList operations for all browsers
        const classListMethods = ['add', 'remove', 'toggle', 'contains'];
        classListMethods.forEach(method => {
            const original = DOMTokenList.prototype[method];
            if (original) {
                DOMTokenList.prototype[method] = function(...args) {
                    try {
                        return original.apply(this, args);
                    } catch (e) {
                        return false;
                    }
                };
            }
        });
        
        // Fix module errors safely
        setTimeout(() => {
            const moduleScripts = document.querySelectorAll(
                'script[src*="shop-cart-sync"], script[src*="chunk.common"]'
            );
            moduleScripts.forEach(script => {
                if (script.src && !script.type) {
                    script.setAttribute('type', 'application/javascript');
                }
            });
        }, 1000);
        
        // Global error handler
        window.addEventListener('error', (e) => {
            const errorMsg = e.message.toLowerCase();
            if (errorMsg.includes('classlist') || 
                errorMsg.includes('lazyload') ||
                errorMsg.includes('undefined')) {
                e.preventDefault();
                return true;
            }
        });
        
        // Promise rejection handler
        window.addEventListener('unhandledrejection', (e) => {
            e.preventDefault();
        });
    }
    
    // ADVANCED SPEED OPTIMIZATIONS
    function optimizeSpeed() {
        // Preload critical images
        const criticalImages = document.querySelectorAll(
            '.hero-banner img, .header__logo img, .site-logo img'
        );
        
        criticalImages.forEach(img => {
            if (img.src) {
                try {
                    const link = document.createElement('link');
                    link.rel = 'preload';
                    link.href = img.src;
                    link.as = 'image';
                    link.crossOrigin = 'anonymous';
                    document.head.appendChild(link);
                } catch (e) {
                    // Silent fail
                }
            }
        });
        
        // DNS prefetch for CDN
        try {
            const dnsPrefetch = document.createElement('link');
            dnsPrefetch.rel = 'dns-prefetch';
            dnsPrefetch.href = 'https://cdn.shopify.com';
            document.head.appendChild(dnsPrefetch);
            
            // Preconnect for faster connections
            const preconnect = document.createElement('link');
            preconnect.rel = 'preconnect';
            preconnect.href = 'https://cdn.shopify.com';
            preconnect.crossOrigin = 'anonymous';
            document.head.appendChild(preconnect);
        } catch (e) {
            // Silent fail
        }
        
        // iOS Safari specific optimizations
        if (isIOS) {
            // Force hardware acceleration
            const iosStyles = `
                .lazyload {
                    -webkit-transform: translate3d(0,0,0);
                    transform: translate3d(0,0,0);
                }
            `;
            const iosStyleSheet = document.createElement('style');
            iosStyleSheet.textContent = iosStyles;
            document.head.appendChild(iosStyleSheet);
        }
    }
    
    // MEMORY OPTIMIZATION FOR MOBILE
    function optimizeMemory() {
        // Clean up event listeners on page hide (for iOS)
        if (isIOS) {
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    // Page is hidden, can cleanup if needed
                }
            });
        }
        
        // Limit concurrent image loads on slow connections
        if (isSlowConnection) {
            const originalImage = window.Image;
            let concurrentLoads = 0;
            const maxConcurrent = 2;
            
            window.Image = function() {
                const img = new originalImage();
                const originalSrc = Object.getOwnPropertyDescriptor(originalImage.prototype, 'src');
                
                Object.defineProperty(img, 'src', {
                    get: function() {
                        return originalSrc.get.call(this);
                    },
                    set: function(value) {
                        if (concurrentLoads >= maxConcurrent) {
                            setTimeout(() => {
                                originalSrc.set.call(this, value);
                            }, 100);
                        } else {
                            concurrentLoads++;
                            img.onload = img.onerror = () => {
                                concurrentLoads--;
                            };
                            originalSrc.set.call(this, value);
                        }
                    }
                });
                
                return img;
            };
        }
    }
    
    // SAFE INITIALIZATION
    function initMahukaaFixes() {
        try {
            injectStyles();
            fixAllErrors();
            optimizeSpeed();
            optimizeMemory();
            new MahukaaLazyLoad();
            
            console.log('Mahukaa Universal Optimizations Active');
        } catch (error) {
            // Silent initialization failure
        }
    }
    
    // SMART STARTUP FOR ALL DEVICES
    function startOptimizations() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                setTimeout(initMahukaaFixes, CONFIG.LOAD_DELAY);
            });
        } else {
            setTimeout(initMahukaaFixes, CONFIG.LOAD_DELAY);
        }
        
        // Fallback for complete load
        window.addEventListener('load', () => {
            setTimeout(() => {
                // Ensure all target images are loaded
                document.querySelectorAll('img[data-src].lazyload').forEach(img => {
                    if (!img.classList.contains('lazyloaded')) {
                        img.src = img.dataset.src;
                        img.classList.add('lazyloaded');
                    }
                });
            }, 2000);
        });
    }
    
    // GLOBAL FUNCTIONS
    window.mahukaaLoadAllImages = function() {
        document.querySelectorAll('img[data-src]').forEach(img => {
            img.src = img.dataset.src;
            img.classList.add('lazyloaded');
        });
    };
    
    window.mahukaaGetStats = function() {
        const total = document.querySelectorAll('img[data-src]').length;
        const loaded = document.querySelectorAll('.lazyloaded').length;
        return {
            total,
            loaded,
            percentage: total > 0 ? Math.round((loaded / total) * 100) : 0,
            device: isIOS ? 'iOS' : isSafari ? 'Safari' : 'Other',
            slowConnection: isSlowConnection
        };
    };
    
    // START OPTIMIZATIONS
    startOptimizations();
    
})();
//I request you all Do not change this code this may cause speed
// Load all images immediately
//mahukaa.loadAllImages();

// Clear image cache
//mahukaa.clearCache();

// Get loading statistics
//mahukaa.getStats();

// Disable Right Click
document.addEventListener('contextmenu', event => event.preventDefault());

// Disable F12, Ctrl+Shift+I/J/U, Ctrl+S
document.onkeydown = function(e) {
    // F12
    if (e.keyCode == 123) {
        return false;
    }
    // Ctrl + Shift + I
    if (e.ctrlKey && e.shiftKey && e.keyCode == 73) {
        return false;
    }
    // Ctrl + Shift + J
    if (e.ctrlKey && e.shiftKey && e.keyCode == 74) {
        return false;
    }
    // Ctrl + U (view source)
    if (e.ctrlKey && e.keyCode == 85) {
        return false;
    }
    // Ctrl + S (save page)
    if (e.ctrlKey && e.keyCode == 83) {
        return false;
    }
    // Ctrl + Shift + C (Inspect element)
    if (e.ctrlKey && e.shiftKey && e.keyCode == 67) {
        return false;
    }
};

// Disable DevTools open (resize detection)
// setInterval(function () {
//     if (window.outerWidth - window.innerWidth > 200 ||
//         window.outerHeight - window.innerHeight > 200) {
//         document.body.innerHTML = "<h2 style='text-align:center;margin-top:40px;'>Developer tools disabled</h2>";
//     }
// }, 500);
